import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAPIErrorMessage } from '../../../../../helpers/helpers';
import { useSidebarData } from '../../../../../hooks/useSidebarData';
import { CompanySpacesService } from '../../../../../service/CompanySpacesService';
import { FlowService } from '../../../../../service/FlowService';
import { FolderService } from '../../../../../service/FolderService';
import { FormsService } from '../../../../../service/FormService';
import { UserService } from '../../../../../service/UserService';
import { useAuthStore } from '../../../../../store/storeAuth';
import useFlowsStore from '../../../../../store/storeFlows';
import { useFormTabStore } from '../../../../../store/storeFormTabs';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../../store/storeNotifications';
import { useObjectStore } from '../../../../../store/storeObjects';
import { Namespace } from '../../../../../types/namespace';
import { Flows, Folder } from '../../../../../util/Types/Folder';
import { UserObject } from '../../../../../util/Types/User';
import type { FlowModalActions } from '../../FlowModalContext';
import { useVectorMemoryDeletion } from '../../shared/useVectorMemoryDeletion';
import { isFormFlow } from '../../utils';
import { FluxObject } from '../context';

export interface VectorDeleteDialogControl {
  openDialog: (ns: Namespace) => void;
}

export function useAgentsSidebarHandlers(
  activeFlowId: string | undefined,
  flowModal: FlowModalActions,
  vectorDelete: VectorDeleteDialogControl,
) {
  const navigate = useNavigate();
  const setNotification = useNotificationStore(s => s.setNotification);
  const {
    refresh: refreshSidebar,
    removeFlow: removeFlowOptimistic,
    updateSpace,
    spaces,
  } = useSidebarData();
  const { setFlows, flows: storeFlows } = useFlowsStore(s => s);
  const { removeFormTabWithNavigation } = useFormTabStore();
  const { user: authUser } = useAuthStore(s => s);
  const { setObjectUpdate, removeObject, setObjects } = useObjectStore(s => s);
  const [editingObject, setEditingObject] = useState<FluxObject | null>(null);
  const [editingVectorMemory, setEditingVectorMemory] =
    useState<Namespace | null>(null);
  const [createInterfaceFlow, setCreateInterfaceFlow] = useState<{
    flow: Flows;
    folderId: string;
  } | null>(null);
  const [libraryFlow, setLibraryFlow] = useState<{
    flow: Flows;
    folderId: string;
  } | null>(null);
  const [isCreateFormLoading, setIsCreateFormLoading] = useState(false);

  const setEditingObjectAndClose = useCallback(
    () => setEditingObject(null),
    [],
  );
  const setEditingVectorMemoryAndClose = useCallback(
    () => setEditingVectorMemory(null),
    [],
  );

  const handleFlowRename = useCallback(
    (flow: Flows, folderId: string) => {
      flowModal.openRenameFlow(flow, folderId);
    },
    [flowModal],
  );

  const handleFlowDelete = useCallback(
    async (flow: Flows) => {
      const isForm = isFormFlow(flow);
      const isFluxbot = flow.type === 'chat';
      const itemType = isForm ? 'interface' : isFluxbot ? 'fluxbot' : 'agent';
      if (
        !window.confirm(
          `Are you sure you want to delete this ${itemType}? This action cannot be undone.`,
        )
      ) {
        return;
      }
      try {
        removeFlowOptimistic(flow.id);
        setFlows(storeFlows.filter(f => f.id !== flow.id));
        if (isForm) {
          await FormsService.deleteForm(flow.id);
          removeFormTabWithNavigation(flow.id, navigate);
        } else {
          await FlowService.deleteFlow(flow.id, isFluxbot ? 'chat' : undefined);
        }
        setNotification({
          type: NotificationType.Success,
          message: isForm
            ? 'Interface deleted successfully'
            : isFluxbot
              ? 'Fluxbot deleted successfully'
              : 'Agent deleted successfully',
        });
        if (activeFlowId === flow.id && !isForm) navigate('/dashboard-beta');
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [
      setNotification,
      activeFlowId,
      navigate,
      refreshSidebar,
      setFlows,
      storeFlows,
      removeFlowOptimistic,
      removeFormTabWithNavigation,
    ],
  );

  const handleFlowShare = useCallback(
    (flow: Flows, folderId: string) => {
      flowModal.openShareFlow(flow, folderId);
    },
    [flowModal],
  );

  const handleFlowOpenInNewTab = useCallback((flow: Flows) => {
    const isForm = isFormFlow(flow);
    const url = isForm
      ? `/dashboard-beta/interface/${flow.id}`
      : `/dashboard-beta/agent/${flow.id}`;
    window.open(url, '_blank');
  }, []);

  const handleCreateInterface = useCallback((flow: Flows, folderId: string) => {
    setCreateInterfaceFlow({ flow, folderId });
  }, []);

  const handleCreateForm = useCallback(
    async (
      name: string,
      useAgent: boolean,
      flowId: string,
      spaceId?: string,
    ) => {
      setIsCreateFormLoading(true);
      try {
        const response = await FormsService.createForm({
          name,
          flowId,
          folderId: createInterfaceFlow?.folderId || '',
          index: 1,
          useAgent,
        });
        navigate(
          `/dashboard-beta/interface/${response.data.id}${
            spaceId ? `?spaceId=${spaceId}` : ''
          }`,
        );
        setCreateInterfaceFlow(null);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: 'Interface created successfully',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      } finally {
        setIsCreateFormLoading(false);
      }
    },
    [createInterfaceFlow, navigate, refreshSidebar, setNotification],
  );

  const handleAddToLibrary = useCallback((flow: Flows, folderId: string) => {
    setLibraryFlow({ flow, folderId });
  }, []);

  const handleFlowDuplicate = useCallback(
    async (flow: Flows, folderId: string) => {
      try {
        await flowModal.duplicateFlow(flow, folderId);
        setNotification({
          type: NotificationType.Success,
          message: isFormFlow(flow)
            ? 'Interface duplicated successfully'
            : flow.type === 'chat'
              ? 'Fluxbot duplicated successfully'
              : 'Agent duplicated successfully',
        });
      } catch {
        return;
      }
    },
    [flowModal, setNotification],
  );

  const { deleteVectorMemory } = useVectorMemoryDeletion({
    spaces,
    updateSpace,
    refreshSidebar,
  });
  const performVectorMemoryDelete = deleteVectorMemory;

  const handleVectorMemoryDelete = useCallback(
    (ns: Namespace) => {
      vectorDelete.openDialog(ns);
    },
    [vectorDelete],
  );

  const handleObjectEdit = useCallback(
    (obj: FluxObject) => {
      const fullObject: UserObject | undefined =
        authUser?.objects?.find(o => o.id === obj.id) ||
        (spaces.flatMap(s => s.objects || []).find(o => o.id === obj.id) as
          | UserObject
          | undefined);
      if (fullObject) {
        setObjectUpdate({
          isCreating: false,
          name: fullObject.name,
          id: fullObject.id,
          sessions: fullObject.sessions,
          text: fullObject.text,
          localObject: fullObject.localObject,
          sessionEnabled: fullObject.sessionEnabled,
          flowId: fullObject.flowId,
        });
        setEditingObject(fullObject as FluxObject);
      }
    },
    [authUser, spaces, setObjectUpdate],
  );

  const handleObjectRename = useCallback(
    (obj: FluxObject) => handleObjectEdit(obj),
    [handleObjectEdit],
  );

  const handleObjectDelete = useCallback(
    async (obj: FluxObject) => {
      if (!window.confirm('Are you sure you want to delete this object?'))
        return;
      try {
        const spaceId = spaces.find(s =>
          s.objects?.some(o => o.id === obj.id),
        )?.id;
        const actualSpaceId = spaceId || 'user';
        removeObject(obj.id, actualSpaceId);
        if (spaceId) {
          const space = spaces.find(s => s.id === spaceId);
          const newObjects = (space?.objects || []).filter(
            o => o.id !== obj.id,
          );
          await CompanySpacesService.updateCompanySpace(
            authUser?.selectedCompanyId!,
            { spaceId, objects: newObjects },
          );
          setObjects(newObjects as UserObject[], spaceId);
        } else {
          const newObjects = (authUser?.objects || []).filter(
            o => o.id !== obj.id,
          );
          await UserService.updateUser({ objects: newObjects });
          refreshSidebar();
          setObjects(newObjects, 'user');
        }
        setNotification({
          type: NotificationType.Success,
          message: 'Object deleted successfully',
        });
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [
      authUser,
      spaces,
      setNotification,
      refreshSidebar,
      setObjects,
      removeObject,
    ],
  );

  const handleVectorMemoryEdit = useCallback((ns: Namespace) => {
    setEditingVectorMemory(ns);
  }, []);

  const handleFolderRename = useCallback(
    async (folder: Folder) => {
      const newName = prompt('Enter new name:', folder.name);
      if (!newName || newName.trim() === folder.name) return;
      try {
        await FolderService.updateFolder(folder.id, { name: newName.trim() });
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: 'Folder renamed successfully',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleFolderDelete = useCallback(
    async (folder: Folder) => {
      if (
        !window.confirm(
          'Are you sure you want to delete this folder? This action cannot be undone.',
        )
      )
        return;
      try {
        await FolderService.deleteFolder(folder.id);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: 'Folder deleted successfully',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleAddFolder = useCallback(
    async (spaceId?: string) => {
      const name = prompt('Enter folder name:');
      if (!name?.trim()) return;
      try {
        if (spaceId) {
          await FolderService.createFolderOnSpace({
            name: name.trim(),
            spaceId,
            section: 'agents',
          });
        } else {
          await FolderService.createFolder({
            name: name.trim(),
            section: 'agents',
          });
        }
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: 'Folder created successfully',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleEditSpace = useCallback(
    (spaceId: string) => {
      navigate(window.location.pathname + '?space=true&spaceId=' + spaceId);
      window.dispatchEvent(new CustomEvent('open-settings-modal'));
    },
    [navigate],
  );

  const handleArchiveSpace = useCallback(
    async (spaceId: string) => {
      if (!window.confirm('Are you sure you want to archive this space?'))
        return;
      try {
        await CompanySpacesService.disableSpace(spaceId);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: 'Space archived successfully',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  return {
    handlers: {
      handleFlowRename,
      handleFlowDelete,
      handleFlowShare,
      handleFlowOpenInNewTab,
      handleCreateInterface,
      handleCreateForm,
      handleAddToLibrary,
      handleFlowDuplicate,
      handleObjectEdit,
      handleObjectRename,
      handleObjectDelete,
      handleVectorMemoryEdit,
      handleVectorMemoryDelete,
      handleFolderRename,
      handleFolderDelete,
      handleAddFolder,
      handleEditSpace,
      handleArchiveSpace,
    },
    modals: {
      editingObject,
      setEditingObject,
      editingVectorMemory,
      setEditingVectorMemory,
      createInterfaceFlow,
      setCreateInterfaceFlow,
      libraryFlow,
      setLibraryFlow,
      isCreateFormLoading,
    },
    setEditingObjectAndClose,
    setEditingVectorMemoryAndClose,
    performVectorMemoryDelete,
  };
}
